import { useMemo } from "react";
import { Eye, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { useAppSelector } from "@/stores/hook";
import { isImpersonating, readActorSession } from "./impersonationStorage";
import { switchBackToActor } from "./impersonationActions";

/**
 * Height of the "Viewing as …" strip. The app shell sizes every page from
 * `--header-height` (4rem app bar), so while the strip is mounted the main
 * column's variable becomes {@link HEADER_HEIGHT_WITH_STRIP} — set on
 * `SidebarInset` (see AppLayout), NOT on `:root`, so the sidebar's own header
 * keeps its 4rem and only the column that actually shows the strip grows.
 */
export const IMPERSONATION_STRIP_HEIGHT = "2.25rem";
export const HEADER_HEIGHT_WITH_STRIP = "6.25rem";

function formatSince(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime()) || d.getTime() === 0) return null;
  const sameDay = d.toDateString() === new Date().toDateString();
  return d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    ...(sameDay ? {} : { day: "numeric", month: "short" }),
  });
}

/**
 * The persistent reminder that this session is borrowed: who you are viewing
 * as, since when, and the one-click way back. Rendered inside the app bar
 * (Header) so it is on every screen and can never be scrolled away.
 */
export default function ImpersonationBanner() {
  const viewed = useAppSelector((state) => state.auth.currentUser?.user);
  const actor = useMemo(() => readActorSession(), []);
  if (!actor) return null;

  const viewedName: string = viewed?.name ?? actor.viewing.name ?? "this user";
  const roleName: string | null = viewed?.role?.name ?? actor.viewing.roleName;
  const since = formatSince(actor.startedAt);
  const backLabel = actor.user.name
    ? `Switch back to ${actor.user.name}`
    : "Switch back to my account";

  const handleBack = async () => {
    toast.loading("Switching back…", { id: "switch-user" });
    const ok = await switchBackToActor();
    if (!ok) {
      toast.error("Couldn't find your own session. Please sign in again.", {
        id: "switch-user",
      });
    }
  };

  return (
    <div
      role="status"
      data-testid="impersonation-banner"
      style={{ height: IMPERSONATION_STRIP_HEIGHT }}
      className="flex w-full items-center justify-between gap-4 border-b border-[#f1dcae] bg-[#fff6e3] px-6 text-[12.5px] text-[#6b4700]"
    >
      <div className="flex min-w-0 items-center gap-2">
        <Eye className="size-4 shrink-0 text-[#b7791f]" />
        <span className="truncate">
          Viewing as{" "}
          <span className="font-semibold text-[#4a3000]">{viewedName}</span>
          {roleName ? (
            <span className="text-[#8a6a2a]"> · {roleName}</span>
          ) : null}
          <span className="hidden text-[#8a6a2a] md:inline">
            {" "}
            — everything you see and do here is as them
            {since ? ` · since ${since}` : ""}
          </span>
        </span>
      </div>
      <button
        type="button"
        onClick={() => void handleBack()}
        className="flex shrink-0 items-center gap-1.5 rounded-lg border border-[#e2c78a] bg-white px-2.5 py-1 text-[12px] font-medium text-[#6b4700] shadow-sm transition-colors hover:bg-[#fff1d6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e2c78a]"
      >
        <Undo2 className="size-3.5" />
        {backLabel}
      </button>
    </div>
  );
}

/**
 * Render-time check. A switch always reloads the page, so the answer cannot
 * change during a mount — memoised once, no subscription needed.
 */
export function useIsImpersonating(): boolean {
  return useMemo(() => isImpersonating(), []);
}
